<script lang="ts">
  import { onMount } from "svelte"
  import CTA from "@kontextwerk/web-sdk/ui/actions/CTA.svelte"
  import { formatEuroCents } from "../../_shared/_logic/backlog-estimation"
  import ModalDialog from "@kontextwerk/web-sdk/ui/overlay/ModalDialog.svelte"
  import type { RowDetails } from "./_logic/details"

  const {
    details,
    onClose,
    focusCalculation = false,
    onUseCalculatedDuration,
  }: {
    details: RowDetails
    onClose: () => void
    focusCalculation?: boolean
    onUseCalculatedDuration?: () => void
  } = $props()
  let calculationSection: HTMLElement
  onMount(() => {
    if (!focusCalculation) return

    const frame = requestAnimationFrame(() => {
      calculationSection?.focus({ preventScroll: true })
      calculationSection?.scrollIntoView({ block: "start" })
    })

    return () => cancelAnimationFrame(frame)
  })
</script>

<ModalDialog appearance="detail" title="Details zur Planungszeile" size="xl" {onClose}>
    {#snippet descriptionSnippet()}
        <span>{details.address}</span>
    {/snippet}
    <div class="row-details">
        <section aria-label="Aktuelle Auswahl">
            <h3>Aktuelle Auswahl</h3>
            <dl class="selection">
                <div>
                    <dt>Liegenschaft</dt>
                    <dd>{details.address}</dd>
                </div>
                <div>
                    <dt>Einheit</dt>
                    <dd>{details.unit}</dd>
                </div>
                <div>
                    <dt>Auftragstypen</dt>
                    <dd>{details.orderTypes.join(", ") || "Noch keine ausgewählt"}</dd>
                </div>
            </dl>
            <p class="muted">{details.selectionOrigin}</p>
        </section>
        <section
      class="calculation"
      aria-label="Preis und Dauer"
      tabindex="-1"
      bind:this={calculationSection}
    >
            <h3>Preis und Dauer</h3>
            <dl class="calculation-summary">
                <div>
                    <dt>Listenpreis{details.estimate?.totalPrice ? " (Gesamtpreis)" : ""}</dt>
                    <dd>
                        {details.estimate?.priceCents == null
              ? "Unbekannt"
              : formatEuroCents(details.estimate.priceCents)}
                    </dd>
                </div>
                <div>
                    <dt>Aktuelle Dauer</dt>
                    <dd
            >{details.currentDuration
              ? details.currentDuration + " Minuten"
              : "Noch keine Dauer"}</dd
          >
                </div>
            </dl>
            {#if details.manualDuration}
                <p>
                    {details.currentDuration || "Keine"} Minuten von Ihnen vorgegeben.{details.estimate
            ?.durationMinutes
            ? " Berechneter Vorschlag: " + details.estimate.durationMinutes + " Minuten."
            : ""}
                </p>
            {/if}
            <p>
                {details.estimate?.explanation ??
          "Für diesen älteren Entwurf fehlen Berechnungsangaben. Bitte die Datei erneut hochladen."}
            </p>
            {#if onUseCalculatedDuration && details.estimate?.durationMinutes}
                <CTA
          type="ghostButtonReverse"
          darkMode={false}
          content="Berechnete Dauer übernehmen"
          onClick={onUseCalculatedDuration}
        />
            {/if}
            {#if details.estimate?.parts.length}
                <details>
                    <summary>Preisbestandteile anzeigen</summary>
                    <table class="price-parts">
                        <thead
              ><tr><th>Spalte aus der Liste</th><th>Gelesener Wert</th><th>Berücksichtigung</th></tr
              ></thead
            >
                        <tbody
              >{#each details.estimate.parts as part (part.id)}
                                <tr
                  ><th scope="row">{part.column}</th><td data-label="Gelesener Wert"
                    >{part.value || "Leer"}</td
                  ><td data-label="Berücksichtigung">{part.disposition}</td></tr
                >
                            {/each}</tbody
            >
                    </table>
                </details>
            {/if}
        </section>
        {#if details.hints.length}
            <section class="hints" aria-label="Hinweise zur Zeile">
                <h3>Bitte beachten</h3>
                <ul>
                    {#each details.hints as hint, index (index)}<li>{hint}</li>{/each}
                </ul>
            </section>
        {/if}
        <section aria-label="Angaben aus der Liste">
            <h3>Angaben aus der Liste</h3>
            {#if details.sources.length}
                <p class="file">{details.fileName} · {details.sourceSystem}</p>
            {/if}
            <p class="muted">{details.sourceNote}</p>
            {#each details.sources as source (source.id)}
                <section class="source" aria-label={source.location}>
                    <h4>{source.location}</h4>
                    <p>{source.matching}</p>
                    {#if source.matchWarnings?.length}
                        <ul aria-label="Hinweise zur ursprünglichen Zuordnung">
                            {#each source.matchWarnings as hint, index (index)}<li>{hint}</li>{/each}
                        </ul>
                    {/if}
                    {#if source.fields.length}
                        <table>
                            <thead
                ><tr
                  ><th>Spalte aus der Liste</th><th>Gelesener Wert</th><th
                    >Bedeutung für die Planung</th
                  ></tr
                ></thead
              >
                            <tbody>
                                {#each source.fields as field (field.id)}
                                    <tr class={{ uncertain: field.uncertain }}>
                                        <th scope="row">{field.label}</th>
                                        <td class="source-value" data-label="Gelesener Wert">{field.value}</td>
                                        <td data-label="Bedeutung für die Planung">
                                            {#each field.explanations as explanation, index (index)}<p>
                                                    {explanation}
                                                </p>{/each}
                                        </td>
                                    </tr>
                                {/each}
                            </tbody>
                        </table>
                    {/if}
                </section>
            {/each}
        </section>
    </div>
</ModalDialog>

<style>
  .row-details {
    --text-100: var(--text-invers-100);
    --text: var(--text-invers-100);
    min-width: 0;
  }
  .row-details,
  .row-details :is(h3, h4, p, dt, dd, li, th, td, summary) {
    color: var(--text-invers-100) !important;
  }
  .calculation {
    scroll-margin-top: 16px;
  }
  .calculation-summary {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 12px 24px;
    margin: 0 0 12px;
  }
  summary {
    cursor: pointer;
    padding-block: 12px;
    font-weight: 600;
  }
  section + section {
    margin-top: 24px;
  }
  h3 {
    font-size: 18px;
    line-height: 1.4;
    margin: 0 0 12px;
  }
  h4 {
    font-size: 15px;
    margin: 0 0 8px;
  }
  p {
    margin: 0 0 8px;
    overflow-wrap: anywhere;
  }
  .file {
    font-weight: 600;
  }
  .selection {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 12px 24px;
    margin: 0 0 12px;
  }
  .selection > div:last-child {
    grid-column: 1 / -1;
  }
  dt {
    font-size: 13px;
    margin-bottom: 4px;
  }
  dd {
    margin: 0;
    overflow-wrap: anywhere;
  }
  .hints {
    border-left: 3px solid #ac741d;
    padding: 0 0 0 16px;
  }
  ul {
    margin: 0;
    padding-left: 20px;
    overflow-wrap: anywhere;
  }
  li + li {
    margin-top: 8px;
  }
  .source {
    border-top: 1px solid #d6dde1;
    padding-top: 16px;
    margin-top: 16px;
  }
  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
    margin-top: 16px;
    font-size: 14px;
  }
  th,
  td {
    text-align: left;
    vertical-align: top;
    padding: 12px;
    border-bottom: 1px solid #d6dde1;
    overflow-wrap: anywhere;
  }
  thead th {
    background: #f2f5f6;
    font-size: 13px;
  }
  th:first-child {
    width: 24%;
  }
  th:nth-child(2) {
    width: 28%;
  }
  tbody th {
    font-weight: 600;
  }
  td p:last-child {
    margin-bottom: 0;
  }
  .source-value {
    white-space: pre-wrap;
  }
  @media (max-width: 640px) {
    .selection {
      grid-template-columns: 1fr;
    }
    table,
    tbody,
    tr,
    th,
    td {
      display: block;
      width: 100%;
    }
    thead {
      display: none;
    }
    tr {
      border-bottom: 1px solid #d6dde1;
      padding: 12px 0;
    }
    th,
    td {
      border: 0;
      padding: 4px 0;
    }
    th:first-child {
      width: 100%;
    }
    td::before {
      content: attr(data-label);
      display: block;
      font-size: 12px;
      color: var(--text-invers-100);
      margin-bottom: 4px;
    }
  }
</style>
